import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Plus, Loader2, Image as ImageIcon, Users, X,
  Clock, CheckCircle2, XCircle, AlertCircle, Trash2, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BODY = 1600; // 10x 160-char SMS segments — Twilio's hard upper bound

const SEGMENT_OPTIONS = [
  { value: 'all', label: 'All opted-in subscribers' },
  { value: 'event_buyers', label: 'Buyers of this event' },
  { value: 'engaged', label: 'SMS-engaged (replied or opted in via SMS)' },
  { value: 'crm_tag', label: 'CRM Tag…' },
  { value: 'city', label: 'City…' },
];

export default function SMSBlastTab({ event, token }) {
  const eventId = event?.event_id;
  const headers = { Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState(null);
  const [blasts, setBlasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlast, setEditingBlast] = useState(null);
  const [preview, setPreview] = useState({ count: null, sample: [], estimated_cost_usd: 0 });
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [liveStats, setLiveStats] = useState({});

  const blankForm = {
    name: '',
    body: '',
    media_url: '',
    segment: { kind: 'event_buyers', event_id: eventId },
  };
  const [form, setForm] = useState(blankForm);

  // ----- Fetchers -----
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [eventId]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        axios.get(`${API}/admin/sms/status`, { headers }),
        axios.get(`${API}/admin/sms-blasts${eventId ? `?event_id=${eventId}` : ''}`, { headers }),
      ]);
      setStatus(s.data);
      setBlasts(b.data.blasts || []);
    } catch (e) {
      toast.error('Could not load SMS blasts');
    } finally { setLoading(false); }
  };

  const runPreview = async () => {
    if (!form.segment?.kind) return;
    setPreviewing(true);
    try {
      const r = await axios.post(
        `${API}/admin/sms-blasts/segment/preview`,
        { segment: form.segment, media_url: form.media_url || null },
        { headers },
      );
      setPreview(r.data);
    } catch { toast.error('Preview failed'); }
    finally { setPreviewing(false); }
  };

  const segmentSummary = (seg) => {
    if (!seg) return '';
    const o = SEGMENT_OPTIONS.find(x => x.value === seg.kind);
    let extras = '';
    if (seg.kind === 'crm_tag') extras = ` — ${seg.tag || '?'}`;
    if (seg.kind === 'city') extras = ` — ${seg.city || '?'}`;
    if (seg.kind === 'event_buyers') extras = event?.name ? ` — ${event.name}` : '';
    return (o?.label || seg.kind) + extras;
  };

  const saveDraft = async () => {
    if (!form.body.trim()) { toast.error('Message body required'); return; }
    setSending(true);
    try {
      const payload = { ...form, event_id: eventId };
      const r = editingBlast
        ? await axios.patch(`${API}/admin/sms-blasts/${editingBlast.blast_id}`, payload, { headers })
        : await axios.post(`${API}/admin/sms-blasts`, payload, { headers });
      toast.success(editingBlast ? 'Draft updated' : 'Draft saved');
      setForm(blankForm);
      setEditingBlast(null);
      setShowForm(false);
      await refresh();
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSending(false); }
  };

  const fireBlast = async (blast) => {
    const isMock = status?.mock_mode;
    const verb = isMock ? `Send TEST blast (mock mode, no real SMS will fire)?` : `Send "${blast.name}" now to ${preview.count ?? '?'} recipients?`;
    if (!window.confirm(verb)) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/sms-blasts/${blast.blast_id}/send`, {}, { headers });
      toast.success(isMock ? 'Mock blast queued — no real SMS sent' : 'Blast queued — delivery rolling out');
      pollStats(blast.blast_id);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const pollStats = (blastId) => {
    let tries = 0;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/admin/sms-blasts/${blastId}/stats`, { headers });
        setLiveStats(s => ({ ...s, [blastId]: r.data }));
        if (r.data.status === 'sent' || tries > 30) return;
      } catch {}
      tries += 1;
      setTimeout(tick, 2000);
    };
    tick();
  };

  const deleteDraft = async (blastId) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API}/admin/sms-blasts/${blastId}`, { headers });
      toast.success('Deleted');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="sms-blast-tab">
      {/* Config banner */}
      {status && (
        <Card className={status.mock_mode ? 'bg-amber-500/10 border-amber-500/40' : 'bg-emerald-500/10 border-emerald-500/40'}>
          <CardContent className="p-3 flex items-start gap-3">
            {status.mock_mode ? (
              <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            )}
            <div className="text-xs leading-relaxed flex-1">
              {status.mock_mode ? (
                <span><b>Mock mode active.</b> Twilio env vars not set — blasts will be recorded but no real SMS will be transmitted. Configure <code className="font-mono">TWILIO_ACCOUNT_SID</code>, <code className="font-mono">TWILIO_AUTH_TOKEN</code>, and a <code className="font-mono">TWILIO_MESSAGING_SERVICE_SID</code> to go live.</span>
              ) : (
                <span>Twilio configured. {status.messaging_service_sid ? 'Messaging Service SID (10DLC-ready) present.' : 'Using FROM number — A2P 10DLC required for production.'} </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4" /> SMS Blasts</h3>
          <p className="text-xs text-muted-foreground">Two-way SMS/MMS to opted-in subscribers. Replies show in <a href="/admin/sms-inbox" className="text-indigo-400 underline">SMS Inbox</a>.</p>
        </div>
        {!showForm && (
          <Button size="sm" onClick={() => { setShowForm(true); setEditingBlast(null); setForm({ ...blankForm, segment: { kind: 'event_buyers', event_id: eventId } }); }} data-testid="new-sms-blast-btn">
            <Plus className="h-4 w-4 mr-1" /> New SMS Blast
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Blast name (internal)</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="VIP last-call reminder" data-testid="sms-blast-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Image (MMS, optional)</Label>
                <Input value={form.media_url} onChange={e => setForm({ ...form, media_url: e.target.value })} placeholder="https://..." data-testid="sms-blast-image" />
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Message body</Label>
                <span className={`text-[10px] ${form.body.length > MAX_BODY ? 'text-red-400' : 'text-muted-foreground'}`}>
                  {form.body.length} / {MAX_BODY} · ~{Math.ceil((form.body.length || 1) / 160)} SMS segment{form.body.length > 160 ? 's' : ''}
                </span>
              </div>
              <Textarea
                rows={4}
                value={form.body}
                onChange={e => setForm({ ...form, body: e.target.value })}
                placeholder={`Hey {NAME}! ${event?.name || 'Our show'} is this Friday. Grab the last tickets: https://eventism.com/...`}
                data-testid="sms-blast-body"
              />
              <p className="text-[10px] text-muted-foreground">
                Use <span className="font-mono">{'{NAME}'}</span> to personalise. <b>Reply STOP to unsubscribe</b> is auto-appended (CTIA-required) — don't repeat it manually.
              </p>
            </div>

            {/* Segment picker */}
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><Users className="h-3 w-3" /> Audience</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <select
                  className="h-9 px-2 border rounded-md text-sm bg-background"
                  value={form.segment.kind}
                  onChange={e => setForm({ ...form, segment: { kind: e.target.value, event_id: eventId } })}
                  data-testid="sms-segment-kind"
                >
                  {SEGMENT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                {form.segment.kind === 'crm_tag' && (
                  <Input
                    placeholder="tag slug (e.g. music:jazz)"
                    value={form.segment.tag || ''}
                    onChange={e => setForm({ ...form, segment: { ...form.segment, tag: e.target.value } })}
                  />
                )}
                {form.segment.kind === 'city' && (
                  <Input
                    placeholder="city slug (e.g. denver)"
                    value={form.segment.city || ''}
                    onChange={e => setForm({ ...form, segment: { ...form.segment, city: e.target.value } })}
                  />
                )}
                <Button size="sm" variant="outline" onClick={runPreview} disabled={previewing} data-testid="sms-preview-btn">
                  {previewing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  Preview audience
                </Button>
              </div>
              {preview.count !== null && (
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap" data-testid="sms-preview-result">
                  <span><b className="text-foreground">{preview.count}</b> recipients</span>
                  <span>·</span>
                  <span>Est. cost: <b className="text-foreground">${preview.estimated_cost_usd}</b> ({form.media_url ? 'MMS' : 'SMS'})</span>
                  {preview.sample.length > 0 && (
                    <span>· Sample: {preview.sample.map(p => `••${p.phone_last4}`).join(', ')}</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-2 border-t">
              <Button size="sm" onClick={saveDraft} disabled={sending || !form.body.trim()} data-testid="sms-save-draft">
                {sending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                {editingBlast ? 'Update draft' : 'Save as draft'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditingBlast(null); setForm(blankForm); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Blast list */}
      {blasts.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          No SMS blasts yet for this event. Create your first one above.
        </CardContent></Card>
      ) : blasts.map(b => {
        const stat = liveStats[b.blast_id] || { stats: b.stats };
        const s = stat.stats || {};
        return (
          <Card key={b.blast_id} data-testid={`sms-blast-row-${b.blast_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-semibold">{b.name}</h4>
                    <Badge variant="outline" className="text-[10px]">
                      {b.status === 'sent' ? <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-400" /> :
                       b.status === 'sending' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> :
                       b.status === 'draft' ? <Clock className="h-3 w-3 mr-1" /> :
                       <XCircle className="h-3 w-3 mr-1 text-red-400" />}
                      {b.status}
                    </Badge>
                    {b.media_url && <Badge variant="outline" className="text-[10px]"><ImageIcon className="h-3 w-3 mr-1" />MMS</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 font-mono">{b.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Audience: {segmentSummary(b.segment)}</p>
                </div>
                <div className="text-right space-y-1 flex-shrink-0">
                  {(s.sent || s.failed) ? (
                    <div className="text-[11px] text-muted-foreground">
                      <span className="text-emerald-400 font-semibold">{s.sent || 0}</span> sent · <span className="text-red-400">{s.failed || 0}</span> fail · <span>{s.skipped || 0}</span> skip
                    </div>
                  ) : (
                    <div className="text-[11px] text-muted-foreground">{s.queued || 0} queued</div>
                  )}
                  {b.status === 'draft' && (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setEditingBlast(b); setForm({ name: b.name, body: b.body, media_url: b.media_url || '', segment: b.segment }); setShowForm(true); }}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-400" onClick={() => deleteDraft(b.blast_id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                      <Button size="sm" className="h-7 px-2 text-xs" onClick={() => fireBlast(b)} data-testid={`sms-fire-${b.blast_id}`}>
                        <Send className="h-3 w-3 mr-1" /> Send
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
